//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, {
  KeyboardEvent,
  ReactNode,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import SearchBar from '@components/molecules/search-bar/SearchBar';
import {
  ISearchTypeAheadData,
  SearchTypeAhead,
} from '@components/molecules/search-bar-with-type-ahead/SearchTypeAhead';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { isUndefined } from '@utils/filterPredicates';
import { getFuzzySearch } from '@utils/getFuzzySearch';

export interface SearchBarWithTypeAheadController {
  shouldClosePopover?: boolean;
  shouldOpenPopover?: boolean;
  shouldResetSearch?: boolean;
}

export interface SearchBarWithTypeAheadStateController {
  setSearchValue: (value: string) => void;
}

export interface SearchBarWithTypeAheadProps {
  className?: string;
  ellipsize?: React.ComponentProps<typeof SearchTypeAhead>['ellipsize'];
  ellipsizeSubTitle?: React.ComponentProps<
    typeof SearchTypeAhead
  >['ellipsizeSubTitle'];
  footer?: ReactNode;
  fuzzySearchThreshold?: number;
  getTypeAheadResults: (searchTerm?: string) => Promise<void>;
  isCaseSensitive?: boolean;
  isDisabled?: boolean;
  isLoadingTypeAheadResults: boolean;
  isStatic?: boolean;
  onKeyUp?: (
    event: KeyboardEvent<HTMLInputElement>,
    searchTerm: string,
  ) => SearchBarWithTypeAheadController | undefined;
  onSearchSelection: (searchTerm: ISearchTypeAheadData) => Promise<void>;
  resultsLabel?: string;
  searchBarPlaceholderText: string;
  searchBarTooltip?: string;
  searchEmptyLabel?: string;
  searchPrefixes: string[];
  searchingLabel?: string;
  shouldEnableSearchTermFiltering?: boolean;
  shouldShowHeader?: boolean;
  trackingIdPrefix: string;
  typeAheadResults: ISearchTypeAheadData[];
}

/**
 * The `SearchBarWithTypeAhead` molecule renders a `SearchBar` molecule with a typeahead ActionSheet.
 *
 * By default, additional filtering is performed based on `searchValue`.
 * Set `shouldEnableSearchTermFiltering` to false to disable this client-side filtering, which can be useful if we want to handle filtering on the server side or implement custom filtering logic.
 */
export const SearchBarWithTypeAhead = forwardRef<
  SearchBarWithTypeAheadStateController,
  SearchBarWithTypeAheadProps
>(
  (
    {
      className,
      ellipsize,
      ellipsizeSubTitle,
      footer,
      fuzzySearchThreshold = 0.3,
      getTypeAheadResults,
      isCaseSensitive = false,
      isDisabled = false,
      isLoadingTypeAheadResults,
      isStatic = false,
      onKeyUp,
      onSearchSelection,
      resultsLabel,
      searchBarPlaceholderText,
      searchBarTooltip,
      searchEmptyLabel,
      searchPrefixes,
      searchingLabel,
      shouldEnableSearchTermFiltering = true,
      shouldShowHeader = true,
      trackingIdPrefix,
      typeAheadResults,
    }: SearchBarWithTypeAheadProps,
    ref,
  ) => {
    const [searchValue, setSearchValue] = useState('');
    const [searchPrefix, setSearchPrefix] = useState('');
    const searchBarRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      setSearchValue: (value) => handleSearchValueChange(value),
    }));

    const handleActionSheetClick = (result: ISearchTypeAheadData) => {
      setSearchValue('');
      setSearchPrefix('');
      void onSearchSelection(result);
    };

    const filteredResults =
      shouldEnableSearchTermFiltering && searchValue !== ''
        ? getFuzzySearch({
            isCaseSensitive,
            itemsToSearch: typeAheadResults,
            searchKeys: ['label'],
            searchTerm: searchValue,
            threshold: fuzzySearchThreshold,
          })
        : typeAheadResults;

    const searchTypeAheadActionSheetButtons = filteredResults
      .sort((a, b) => b.count - a.count)
      .map((filteredResult) => ({
        ...filteredResult,
        onClick: () => handleActionSheetClick(filteredResult),
        trackingId: `${trackingIdPrefix}-action-sheet-button`,
      }));

    const handleTypeAheadSearch = (searchTerm?: string) => {
      void getTypeAheadResults(searchTerm?.replace(searchPrefix, ''));
    };

    const handleSearchValueChange = (searchValue: string) => {
      const firstMatchingPrefix = searchPrefixes.find((prefix) =>
        searchValue.startsWith(prefix),
      );

      setSearchPrefix(firstMatchingPrefix ?? '');
      setSearchValue(searchValue);

      // This handles the scenario for reloading the type ahead
      // suggestions when there is no search value. The SearchBar component
      // does not trigger the "handleFetch" when the searchValue is ""
      if (searchValue === '') {
        handleTypeAheadSearch();
      }
    };

    return (
      <Wrapper className={className}>
        <SearchTypeAhead
          actionSheetConfig={searchTypeAheadActionSheetButtons}
          ellipsize={ellipsize}
          ellipsizeSubTitle={ellipsizeSubTitle}
          footer={footer}
          isLoading={isLoadingTypeAheadResults}
          isStatic={isStatic}
          onToggleActionSheet={
            (isOpen) => !isOpen && searchBarRef.current?.blur() // Remove focus from search bar when click outside of action sheet
          }
          placement='bottom'
          resultsLabel={resultsLabel}
          searchEmptyLabel={searchEmptyLabel}
          searchingLabel={searchingLabel}
          searchPrefix={searchPrefix}
          searchTerm={searchValue}
          shouldShowHeader={shouldShowHeader}
        >
          {(open, close) => (
            <SmallTooltip
              appendTarget='body'
              content={searchBarTooltip}
              interactive={false}
              isDisabled={isUndefined(searchBarTooltip)}
              target={
                <SearchBar
                  ref={searchBarRef}
                  handleFetch={(searchTerm) => getTypeAheadResults(searchTerm)}
                  isDisabled={isDisabled}
                  onChange={handleSearchValueChange}
                  onFocus={() => handleTypeAheadSearch(searchValue)}
                  onKeyUp={(event) => {
                    if (event.target instanceof HTMLInputElement) {
                      const controller = onKeyUp?.(event, event.target.value);
                      controller?.shouldResetSearch && setSearchValue('');
                      controller?.shouldOpenPopover && open();
                      controller?.shouldClosePopover && close();
                    }
                  }}
                  placeholder={searchBarPlaceholderText}
                  trackingIdPrefix={trackingIdPrefix}
                  value={searchValue}
                  visuallyHiddenLabel={searchBarPlaceholderText}
                />
              }
            />
          )}
        </SearchTypeAhead>
      </Wrapper>
    );
  },
);
SearchBarWithTypeAhead.displayName = 'SearchBarWithTypeAhead';

const Wrapper = styled.div`
  flex: 1 1 auto;
  position: relative;

  /*
    Applying className over the Popover component only overrides the
    style for the inner content and not the container. This will ensure
    that the width of the typeahead is matching the search bar
  */
  [data-tippy-root] {
    width: 100%;
  }
`;
